import {
  type InvitationFieldsFragment,
  type UserFieldsFragment,
  UserLicenseTypeEnum,
  type UserPlanEnum,
} from '@salescore/client-api'
import { Space, Tag } from 'antd'
import { t } from 'i18next'

// invitationテーブルからplanForSync|Visを消すまでの暫定の型とcheck関数
// 削除後はuserLicensesで統一される
type UserOrInvitation = UserFieldsFragment | InvitationFieldsFragment

interface UserLicenses {
  readonly userLicenses: ReadonlyArray<{ readonly license: string; readonly plan: UserPlanEnum }>
}

// プランの導出に要る形だけを構造的に受ける。fragment 全体を要求すると呼び出し側とテストが fragment を組む必要が出る
type UserPlanSource = UserLicenses | { readonly planForSync: UserPlanEnum; readonly planForVis: UserPlanEnum }

const hasUserLicenses = (x: UserPlanSource): x is UserLicenses => 'userLicenses' in x

/**
 * このコンポーネントがタグを出す順 (SYNC → VISUALIZE) でプランを返す。
 *
 * 表示と同じ順序でソートするために公開している。
 */
export const toUserPlansInDisplayOrder = (
  userOrInvitation: UserPlanSource | undefined,
): readonly [UserPlanEnum | undefined, UserPlanEnum | undefined] => {
  if (userOrInvitation === undefined) {
    return [undefined, undefined]
  }
  if (!hasUserLicenses(userOrInvitation)) {
    return [userOrInvitation.planForSync, userOrInvitation.planForVis]
  }
  const { userLicenses } = userOrInvitation
  return [
    userLicenses.find((x) => x.license === UserLicenseTypeEnum.Sync)?.plan,
    userLicenses.find((x) => x.license === UserLicenseTypeEnum.Visualize)?.plan,
  ]
}

export const UserPlanTag = ({ userOrInvitation }: { userOrInvitation: UserOrInvitation | undefined }) => {
  if (userOrInvitation === undefined) {
    return <></>
  }
  const [syncPlan, visualizePlan] = toUserPlansInDisplayOrder(userOrInvitation)
  return (
    <Space>
      <PlanTag color={`blue`} plan={syncPlan}>
        SYNC
      </PlanTag>
      <PlanTag color={`green`} plan={visualizePlan}>
        VISUALIZE
      </PlanTag>
    </Space>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function PlanTag({ plan, children, color }: { plan: UserPlanEnum | undefined; children: string; color: string }) {
  if (plan === 'none' || plan === undefined) {
    return <></>
  }

  return (
    <Tag style={{ opacity: plan === 'standard' ? 1 : 0.7 }} color={color}>
      {children}
      {plan === 'free' ? t(`(無料)`) : ''}
    </Tag>
  )
}
